import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';

import { cn } from '../utils'
import { Button } from './button'
import { Icon } from './icon'
import { Input, InputProps } from './input'

export const colors = [
  '#EB5757',
  '#F2994A',
  '#F2C94C',
  '#219653',
  '#27AE60',
  '#6FCF97',
  '#2F80ED',
  '#2D9CDB',
  '#56CCF2',
  '#9B51E0',
  '#BB6BD9',
  '#F2F2F2',
]

export const parseColor = (value: string): string => {
  return `#${value.toUpperCase().replace(/[^0-9,A-F]/gi, '')}`;
}

export const ColorPicker = forwardRef<
  HTMLInputElement,
  InputProps & { onChange: (e: string | ChangeEvent<HTMLInputElement>) => void }
>((props, ref) => {
  const handleColorClick = useCallback(
    (c: string) => {
      if (typeof props.onChange === 'function') props.onChange(c)
    },
    [props],
  )

  return (
    <div className="grid grid-cols-4 gap-2">
      {colors.map((c, i) => (
        <button
          type="button"
          className={cn(
            'pointer h-7 w-7 rounded-md',
            props.value === c ? 'ring-ring ring' : 'shadow-outline',
          )}
          style={{ backgroundColor: c }}
          key={i}
          onClick={() => handleColorClick(c)}
        />
      ))}

      <div className="relative col-span-4 w-[100px]">
        <Input ref={ref} {...props} maxLength={7} className="w-full" />
        <div
          className="shadow-outline absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 rounded-md"
          style={{ backgroundColor: `${props.value || ''}` }}
        />
      </div>
    </div>
  )
})

export function ColorPickerV2({
  value,
  options,
  onSelect,
}: {
  value: string
  options: string[]
  onSelect: (value: string) => void
}): JSX.Element {
  const isCustom = !options.includes(value)
  const [customValue, setCustomValue] = useState<string>(value)
  useEffect(() => {
    setCustomValue(value)
  }, [value])

  const isValid = useMemo(() => parseColor(customValue).length === 7, [customValue])
  return (
    <div className="relative grid max-w-[160px] grid-cols-4 items-center gap-3 p-2">
      <div className="group col-span-4 grid grid-cols-4 gap-3">
        {options.map((color) => (
          <button
            className={cn(
              'border-background flex aspect-square w-full cursor-pointer items-center justify-center rounded-full border transition-all hover:scale-125 hover:shadow-[0px_0px_0px_1px_hsl(var(--input-focus-shadow))]',
              {
                'shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))]':
                  color === value,
              },
            )}
            onClick={() => onSelect(color)}
            style={{ backgroundColor: color }}
          >
            <Icon
              name="check"
              className={cn('size-4 stroke-black opacity-0', {
                'opacity-100': color === value,
              })}
            />
          </button>
        ))}
      </div>
      <div
        className={cn(
          'flex aspect-square w-full items-center justify-center rounded-full',
          {
            'border': !isCustom,
            'shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))]': isCustom,
          },
        )}
        style={{
          backgroundColor: isCustom ? customValue : undefined,
        }}
      >
        <Icon
          name="check"
          className={cn('size-4 stroke-black opacity-0', {
            'opacity-100': isCustom,
          })}
        />
      </div>
      <Input
        className="col-span-3 pl-2 font-mono uppercase"
        placeholder="#000000"
        value={customValue}
        maxLength={7}
        onChange={(e) => setCustomValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && customValue !== value && isValid) {
            onSelect(parseColor(customValue))
          }
        }}
      />
      <Button
        size="icon-sm"
        onClick={() => {
          if (isValid) {
            onSelect(parseColor(customValue))
          } else {
            setCustomValue(value)
          }
        }}
        className="absolute right-4 bottom-4 transition-opacity"
        icon={isValid ? 'check' : 'x'}
        variant={isValid ? 'default' : 'secondary'}
      />
    </div>
  )
}
